import React, { useEffect } from "react";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { Platform, View, Text } from "../components/common";
import { AppLayout } from "../components/layout";

// Import the actual dashboard screen
import { SalesRepDashboardScreen } from "../screens/salesrep/SalesRepDashboardScreen";
import { StoreVisitScreen } from "../screens/salesrep/StoreVisitScreen";
import { StoreDetailsScreen } from "../screens/salesrep/StoreDetailsScreen";
import { ResourcesScreen } from "../screens/salesrep/ResourcesScreen";
import { ResourceDetailsScreen } from "../screens/shared/ResourceDetailsScreen";
import { AnnouncementsListScreen } from "../screens/shared/AnnouncementsListScreen";
import { AnnouncementDetailScreen } from "../screens/shared/AnnouncementDetailScreen";
import { StoreResourceScreen } from "../screens/salesrep/StoreResourceScreen";
import { 
  RequestContactScreen,
  ContestEntryScreen
} from "../screens/salesrep/PlaceholderScreens";
import { PayrollScreen } from "../screens/salesrep/PayrollScreen";
import { ProfileScreen } from "../screens/salesrep/ProfileScreen";
import { ShipmentsViewScreen } from "../screens/salesrep/ShipmentsViewScreen";
import { EosSurveyScreen } from "../screens/salesrep/EosSurveyScreen";

// Component to handle redirect after login
const LoginRedirectHandler = () => {
  const navigate = useNavigate();
  
  useEffect(() => {
    const redirectTo = sessionStorage.getItem('loginRedirect');
    if (redirectTo && redirectTo.startsWith('/')) {
      sessionStorage.removeItem('loginRedirect');
      navigate(redirectTo, { replace: true });
      return;
    }
    // Default redirect to dashboard
    navigate('/dashboard', { replace: true });
  }, [navigate]);
  
  return null;
};

// Wrapper for screens in web mode
const WebScreenWrapper = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  return (
    <AppLayout>
      <View style={{ flex: 1, backgroundColor: "#f5f5f5" }}>{children}</View>
    </AppLayout>
  );
};

// Web components
const WebDashboard = (props: any) => (
  <WebScreenWrapper>
    <SalesRepDashboardScreen {...props} />
  </WebScreenWrapper>
);

// Removed WebOrders and WebCustomers as they are not part of current scope
const WebProfile = (props: any) => (
  <WebScreenWrapper>
    <ProfileScreen {...props} />
  </WebScreenWrapper>
);

export const SalesRepNavigator = () => {
  // For web platform
  if (Platform.OS === "web") {
    return (
      <Routes>
        <Route path="/login" element={<LoginRedirectHandler />} />
        <Route path="/dashboard" element={<WebDashboard />} />
        <Route path="/profile" element={<WebProfile />} />
        <Route path="/store-visit" element={<WebScreenWrapper><StoreVisitScreen /></WebScreenWrapper>} />
        <Route path="/store-details/:storeId" element={<WebScreenWrapper><StoreDetailsScreen /></WebScreenWrapper>} />
        <Route path="/store-resource" element={<Navigate to="/resources" replace />} />
        <Route path="/resources" element={<WebScreenWrapper><ResourcesScreen /></WebScreenWrapper>} />
        <Route path="/resources/:resourceId" element={<WebScreenWrapper><ResourceDetailsScreen /></WebScreenWrapper>} />
        <Route path="/announcements" element={<WebScreenWrapper><AnnouncementsListScreen /></WebScreenWrapper>} />
        <Route path="/announcements/:announcementId" element={<WebScreenWrapper><AnnouncementDetailScreen /></WebScreenWrapper>} />
        <Route path="/request-contact" element={<WebScreenWrapper><RequestContactScreen /></WebScreenWrapper>} />
        <Route path="/payroll" element={<WebScreenWrapper><PayrollScreen /></WebScreenWrapper>} />
        <Route path="/contest-entry" element={<WebScreenWrapper><ContestEntryScreen /></WebScreenWrapper>} />
        <Route path="/shipments" element={<WebScreenWrapper><ShipmentsViewScreen /></WebScreenWrapper>} />
        <Route path="/eos-survey" element={<WebScreenWrapper><EosSurveyScreen /></WebScreenWrapper>} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    );
  }

  // For mobile platforms
  return (
    <Routes>
      <Route path="/login" element={<LoginRedirectHandler />} />
      <Route path="/dashboard" element={<AppLayout><SalesRepDashboardScreen /></AppLayout>} />
      <Route path="/profile" element={<AppLayout><ProfileScreen /></AppLayout>} />
      <Route path="/store-visit" element={<AppLayout><StoreVisitScreen /></AppLayout>} />
      <Route path="/store-details/:storeId" element={<AppLayout><StoreDetailsScreen /></AppLayout>} />
      <Route path="/store-resource" element={<Navigate to="/resources" replace />} />
      <Route path="/resources" element={<AppLayout><ResourcesScreen /></AppLayout>} />
      <Route path="/resources/:resourceId" element={<AppLayout><ResourceDetailsScreen /></AppLayout>} />
      <Route path="/announcements" element={<AppLayout><AnnouncementsListScreen /></AppLayout>} />
      <Route path="/announcements/:announcementId" element={<AppLayout><AnnouncementDetailScreen /></AppLayout>} />
      <Route path="/request-contact" element={<AppLayout><RequestContactScreen /></AppLayout>} />
      <Route path="/payroll" element={<AppLayout><PayrollScreen /></AppLayout>} />
      <Route path="/contest-entry" element={<AppLayout><ContestEntryScreen /></AppLayout>} />
      <Route path="/shipments" element={<AppLayout><ShipmentsViewScreen /></AppLayout>} />
      <Route path="/eos-survey" element={<AppLayout><EosSurveyScreen /></AppLayout>} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};
